import React, {
  forwardRef,
  ReactNode,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { HiOutlineFilter } from 'react-icons/hi';
import { MdOutlineDownloading } from 'react-icons/md';
import {
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  Select,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
import { DataTable, DateRangeComp } from 'components';
import SimpleInput from 'components/SimpleInput';
import format from 'date-fns/format';
import {
  useDateSelect,
  useDebounce,
  useAnalyticsEvent,
  useCustomToast,
} from 'hooks';
import { GA_DOWNLOAD_LABEL } from 'utils/constants';
import { Card } from './Card';

const DOWNLOAD_LABEL = 'Baixar planilha';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
interface TableCardsProps extends DataTableProps<any | EntranceExitTable> {
  title?: string;
  customTitle?: ReactNode;
  subtitleMb?: string | number;
  subtitle?: string;
  hasGlobalSearch?: boolean;
  selectOptions?: { id: string; name: string }[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fetchDownload?: any;
  analyticsId?: string;
  setDateRange?: React.Dispatch<React.SetStateAction<DateProps[]>>;
  initDate?: DateProps[];
  isError?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  refetch?: any;
  comeBackId?: { id: string; name: string };
  searchHolder?: string;
  handleFilter?: () => void;
  filter?: string;
}

const TableCard = forwardRef<string | undefined, TableCardsProps>(
  (props, ref) => {
    const {
      title,
      customTitle,
      subtitleMb,
      subtitle,
      hasGlobalSearch,
      selectOptions,
      fetchDownload,
      analyticsId,
      fetchData,
      loading,
      initDate,
      startDate,
      endDate,
      setDateRange,
      isError,
      refetch,
      comeBackId,
      data,
      searchHolder,
      handleFilter,
      filter = '',
      ...rest
    } = props;

    const { handleToast } = useCustomToast();

    const [searchTerm, setSearchTerm] = useState('');
    const [downloadLoading, setDownloadLoading] = useState(false);

    const [selected, setSelected] = useState<{
      id: string;
      name: string;
    } | null>(comeBackId || selectOptions?.[0] || null);

    const handleSelect = useCallback(
      (name: string) => {
        setSelected(
          selectOptions?.filter(option => option.name === name)?.[0] || null,
        );
      },
      [selectOptions],
    );

    const stringToDate = (date: string) => {
      if (date) {
        const regex = /(\d{2}\/\d{2}\/\d{4})/;
        const newDate = date.match(regex)?.[0];

        return new Date(newDate || '');
      }

      return new Date();
    };

    const filtredData = useMemo(() => {
      if (selected && data) {
        const filtredItens = data?.itens.filter(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (item: any) => item.name === selected.name,
        );

        return {
          ...data,
          rows: filtredItens?.length,
          itens: filtredItens.sort(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (a: any, b: any) =>
              stringToDate(b.date).getTime() - stringToDate(a.date).getTime(),
          ),
        };
      } else return data;
    }, [data, selected]);

    const { debouncedValue } = useDebounce(searchTerm);

    const { debouncedValue: debouncedFilter } = useDebounce(filter);

    const { onDateRangeChange } = useDateSelect(setDateRange);

    useImperativeHandle(ref, () => searchTerm, [searchTerm]);

    const analyticsDownloadEvent = useAnalyticsEvent({
      category: 'User',
      action: GA_DOWNLOAD_LABEL,
    });

    const internalOnDownload = async () => {
      if (analyticsId) {
        analyticsDownloadEvent(analyticsId);
      }

      if (fetchDownload) {
        setDownloadLoading(true);
        try {
          await fetchDownload();
          handleToast({
            step: 5,
            action: 'O download do arquivo foi concluído',
          });
        } catch (error) {
          handleToast({
            step: 4,
            action: 'fazer o download do arquivo',
            helpText:
              'Por favor, tente novamente mais tarde ou contate o suporte',
          });
        }
        setDownloadLoading(false);
      }
    };

    useEffect(() => {
      if (!selected && selectOptions) setSelected(selectOptions[0]);
    }, [selectOptions]);

    return (
      <Card {...rest} overflowX="hidden">
        <Flex justifyContent="space-between">
          {customTitle ? (
            customTitle
          ) : (
            <Text
              alignSelf="center"
              color="text.100"
              fontSize="16px"
              fontWeight={400}
            >
              {title}
            </Text>
          )}
          {(hasGlobalSearch ||
            fetchDownload ||
            setDateRange ||
            selectOptions) && (
            <HStack
              ml="32px"
              spacing="18px"
              justify="end"
              w="40%"
              display={{ base: 'none', md: 'flex' }}
            >
              {!isError && setDateRange && (
                <DateRangeComp
                  dateName="table-1"
                  type="month"
                  startDate={initDate}
                  ref={onDateRangeChange}
                  minW="186"
                />
              )}

              {selectOptions && selectOptions?.length > 0 && (
                <Select
                  onChange={event => handleSelect(event.target.value)}
                  value={selected?.name || 'Selecione um centro de custo'}
                  maxW="200px"
                  color="grey.600"
                >
                  {selectOptions.map(option => (
                    <option key={option.name} value={option.name}>
                      {option.name}
                    </option>
                  ))}
                </Select>
              )}

              {hasGlobalSearch && (
                <Box>
                  <SimpleInput
                    isDisabled={isError}
                    aria-label="Buscar informações"
                    id="search"
                    minW="90px"
                    w={handleFilter ? '300px ' : '150px'}
                    onChange={e => setSearchTerm(e.target.value)}
                    placeholder={searchHolder || 'Buscar...'}
                  />
                </Box>
              )}

              {fetchDownload && (
                <HStack>
                  <Button
                    onClick={internalOnDownload}
                    variant="link"
                    rightIcon={
                      downloadLoading ? undefined : <MdOutlineDownloading />
                    }
                  >
                    {DOWNLOAD_LABEL}
                  </Button>
                  {downloadLoading && <Spinner size="xs" color="blue.0" />}
                </HStack>
              )}

              {handleFilter && (
                <Button
                  minW="92px"
                  data-cy="filter"
                  variant="outline"
                  leftIcon={
                    <Icon
                      color="mutable.100"
                      as={HiOutlineFilter}
                      h="20px"
                      w="20px"
                    />
                  }
                  onClick={handleFilter}
                  fontWeight={400}
                >
                  Filtrar
                </Button>
              )}
            </HStack>
          )}
        </Flex>
        <Text
          textAlign="start"
          mb={{ base: '18px', md: subtitleMb || '22px' }}
          color="text.300"
          fontSize="12px"
          fontWeight={500}
        >
          {subtitle || ''}
        </Text>
        {(hasGlobalSearch || fetchDownload || setDateRange) && (
          <VStack
            spacing="18px"
            mb={{ base: '18px', md: subtitleMb || '22px' }}
            align="start"
            w="100%"
            display={{ base: 'flex', md: 'none' }}
          >
            {fetchDownload && (
              <HStack>
                <Button
                  onClick={internalOnDownload}
                  variant="link"
                  rightIcon={
                    downloadLoading ? undefined : <MdOutlineDownloading />
                  }
                >
                  {DOWNLOAD_LABEL}
                </Button>
                {downloadLoading && <Spinner size="xs" color="blue.0" />}
              </HStack>
            )}
            <HStack>
              {!isError && setDateRange && (
                <DateRangeComp
                  dateName="table"
                  type="month"
                  startDate={initDate}
                  ref={onDateRangeChange}
                  minW="186"
                />
              )}
              {selectOptions && selectOptions?.length > 0 && (
                <Select
                  onChange={event => handleSelect(event.target.value)}
                  value={selected?.name || 'Selecione um centro de custo'}
                  maxW="200px"
                  color="grey.600"
                >
                  {selectOptions.map(option => (
                    <option key={option.name} value={option.name}>
                      {option.name}
                    </option>
                  ))}
                </Select>
              )}
              {hasGlobalSearch && (
                <Flex flex={1}>
                  <SimpleInput
                    isDisabled={isError}
                    aria-label="Buscar informações"
                    data-cy="search-1"
                    w={handleFilter ? '210px ' : '150px'}
                    minW="90px"
                    onChange={e => setSearchTerm(e.target.value)}
                    placeholder={searchHolder || 'Buscar...'}
                  />
                </Flex>
              )}
              {handleFilter && (
                <Button
                  minW="92px"
                  data-cy="filter"
                  variant="outline"
                  leftIcon={
                    <Icon
                      color="mutable.100"
                      as={HiOutlineFilter}
                      h="20px"
                      w="20px"
                    />
                  }
                  onClick={handleFilter}
                  fontWeight={400}
                >
                  Filtrar
                </Button>
              )}
            </HStack>
          </VStack>
        )}

        <DataTable
          filter={debouncedFilter}
          data={filtredData}
          selectedOptions={selectOptions}
          selected={selected}
          isError={isError}
          refetch={refetch}
          searchTerm={debouncedValue}
          fetchData={fetchData}
          fromCard
          globalSearch={searchTerm}
          overflowX="auto"
          w="100%"
          loading={loading}
          startDate={
            initDate
              ? format(
                  new Date(initDate[0].startDate || new Date()),
                  'yyyy-MM-dd',
                )
              : startDate
          }
          endDate={
            initDate
              ? format(
                  new Date(initDate[0].endDate || new Date()),
                  'yyyy-MM-dd',
                )
              : endDate
          }
          {...rest}
        />
      </Card>
    );
  },
);

TableCard.displayName = 'TableCard';

export default TableCard;
